import axiosInstance from '../axios/axiosInstance';
import type { PaginatedResponse } from './types';

export interface AuditLogEntry {
  _id: string;
  eventType: string;
  email?: string;
  ipAddress: string;
  userAgent?: string;
  success: boolean;
  details?: Record<string, any>;
  createdAt: string;
  actorName?: string;
}

export interface AuditLogFilters {
  page?: number;
  limit?: number;
  eventType?: string;
  dateRange?: string; // 'all' | '24h' | '7d' | '30d' | '90d'
  search?: string;
  actorType?: string;
}

export const AUDIT_EVENT_LABELS: Record<string, string> = {
  // Authentication
  LOGIN_SUCCESS: 'Login',
  LOGIN_FAILED: 'Login Failed',
  REGISTER_SUCCESS: 'User Registered',
  REGISTER_FAILED: 'Registration Failed',
  TWO_FACTOR_LOGIN_SUCCESS: '2FA Login',
  TWO_FACTOR_LOGIN_FAILED: '2FA Login Failed',
  TWO_FACTOR_ENABLED: '2FA Enabled',
  TWO_FACTOR_DISABLED: '2FA Disabled',
  TWO_FACTOR_RESET: '2FA Reset',
  PASSWORD_RESET_REQUEST: 'Password Reset Requested',
  PASSWORD_RESET_SUCCESS: 'Password Reset Completed',
  PASSWORD_CHANGE: 'Password Changed',
  EMAIL_VERIFICATION: 'Email Verified',
  EMAIL_NOT_VERIFIED: 'Email Not Verified',
  SUSPICIOUS_ACTIVITY: 'Suspicious Activity',

  // User Management
  USER_DELETE: 'User Deleted',
  USER_EMAIL_UPDATED: 'User Email Updated',
  USER_TWO_FACTOR_RESET: 'User 2FA Reset',

  // Resource Management
  PROJECT_DELETE: 'Project Deleted',
  PROJECT_RESTORE: 'Project Restored',
  PROJECT_REQUEST_PUBLISH: 'Project Publish Requested',
  PROJECT_APPROVE_PUBLISH: 'Project Publish Approved',
  PROJECT_PUBLISH: 'Project Published',
  PROJECT_UNPUBLISH: 'Project Unpublished',
  PROJECT_REPUBLISH: 'Project Republished',
  WORKSPACE_DELETE: 'Workspace Deleted',
  WORKSPACE_RESTORE: 'Workspace Restored',
  ORGANIZATION_DELETE: 'Organization Deleted',

  // Member Management
  WORKSPACE_MEMBER_ADDED: 'Workspace Member Added',
  WORKSPACE_MEMBER_REMOVED: 'Workspace Member Removed',
  WORKSPACE_MEMBER_ROLE_UPDATED: 'Workspace Member Role Updated',
  ORGANIZATION_MEMBER_ROLE_UPDATED: 'Organization Member Role Updated',

  // Super Admin Actions
  SUPERADMIN_INVITE: 'Super Admin Invited',
  SUPERADMIN_INVITE_ACCEPTED: 'Super Admin Invite Accepted',
  SUPERADMIN_INVITE_RESENT: 'Super Admin Invite Resent',
  SUPERADMIN_INVITE_SENT: 'Super Admin Invite Sent',
  SUPERADMIN_USER_INVITE: 'User Invited (Admin)',
  SUPERADMIN_USER_REMOVE: 'User Removed (Admin)',
  SUPERADMIN_USER_PASSWORD_RESET: 'Password Reset (Admin)',
  SUPERADMIN_USER_STATUS_UPDATE: 'User Status Changed (Admin)',
  SUPERADMIN_USER_RESTORE: 'User Restored (Admin)',
  SUPERADMIN_ORGANIZATION_SUBSCRIPTION_UPDATE: 'Subscription Updated (Admin)',
  SUPERADMIN_ORGANIZATION_RESTORE: 'Organization Restored (Admin)',
  SUPERADMIN_ORGANIZATION_CREATE: 'Organization Created (Admin)',
  SUPERADMIN_WORKSPACE_RESTORE: 'Workspace Restored (Admin)',
  SUPERADMIN_WORKSPACE_CREATE: 'Workspace Created (Admin)',
  SUPERADMIN_PROJECT_BILLING_UPDATE: 'Billing Status Updated (Admin)',
};

export const auditApi = {
  /**
   * Get all audit log entries with filters and pagination
   */
  getAll: async (params: AuditLogFilters): Promise<PaginatedResponse<AuditLogEntry>> => {
    const { data } = await axiosInstance.get<PaginatedResponse<AuditLogEntry>>('/audit-logs', {
      params,
    });
    return data;
  },
};
